import DieCode from "@/types/die-code";
import type { SwadeModConfigurationInterface } from "@/types/items";

/**
 * One modification-derived operation, held as `{ operator, value, valueType }`.
 *
 * Deliberately dumb: it stores the three fields and nothing more. What they MEAN is interpreted in
 * exactly one place — DieRoll's pipeline. Dice recognition is delegated to DieCode so the formula
 * grammar has a single owner.
 */
export type AdjustmentOperator = 'add' | 'subtract' | 'multiply';
export type AdjustmentValueType = 'step' | 'modify';

export default class DieAdjustment {
    static pattern: RegExp = /^(?:(add|subtract|multiply)\s+)?(\S+)\s+(step|modify)$/i;

    private operator: AdjustmentOperator | null = null;
    private value: string | number | null = null;
    private valueType: AdjustmentValueType | null = null;

    constructor(code: string = '') {
        const parsed = DieAdjustment.pattern.exec((code ?? '').trim());
        if (!parsed) return;

        this.assign(
            (parsed[1]?.toLowerCase() as AdjustmentOperator) ?? 'add',
            parsed[2],
            parsed[3].toLowerCase() as AdjustmentValueType,
        );
    }

    private assign(
        operator: AdjustmentOperator,
        value: string | number,
        valueType: AdjustmentValueType,
    ): void {
        this.operator = operator;
        this.value = value;
        this.valueType = valueType;
    }

    public static fromConfiguration(configuration: SwadeModConfigurationInterface): DieAdjustment {
        const adjustment = new DieAdjustment();

        if (configuration?.value === null || configuration?.value === undefined) {
            return adjustment;
        }

        // `block` and `alter` are legal on SwadeModConfigurationInterface but express nothing this
        // engine can build — `alter` means "set to", not "add". Coercing them to `add` would turn
        // "alter Strength to d8" into a silent "+8", so reject them instead: the adjustment stays
        // unparsed and DieRoll.add() drops it, per the rule that one malformed modification must
        // not blank a character's formula.
        if (configuration.operator !== 'add'
            && configuration.operator !== 'subtract'
            && configuration.operator !== 'multiply') {
            return adjustment;
        }

        const operator: AdjustmentOperator = configuration.operator;

        // `absolute` means "replace the formula", not "append to it" — the same opposition that
        // makes `alter` unusable as `add` in the operator guard above. A null valueType still
        // defaults, because live configurators legitimately emit one.
        if (configuration.valueType !== null
            && configuration.valueType !== undefined
            && configuration.valueType !== 'step'
            && configuration.valueType !== 'modify') {
            return adjustment;
        }

        const valueType: AdjustmentValueType =
            configuration.valueType === 'step' ? 'step' : 'modify';

        adjustment.assign(operator, configuration.value, valueType);

        return adjustment;
    }

    public isParsed(): boolean {
        return this.valueType !== null;
    }

    public getOperator(): AdjustmentOperator | null {
        return this.operator;
    }

    public getValue(): string | number | null {
        return this.value;
    }

    public getValueType(): AdjustmentValueType | null {
        return this.valueType;
    }

    /**
     * Resolves `add`/`subtract` to a sign so the pipeline never branches on the operator. The
     * mapping table's paired rows are one rule with a sign, not two rules.
     */
    public getSign(): 1 | -1 {
        return this.operator === 'subtract' ? -1 : 1;
    }

    /** The value as a DieCode when it is a single dice term, else null. Delegates all parsing. */
    public getDie(): DieCode | null {
        if (!this.isParsed() || this.value === null) return null;

        const code = new DieCode(String(this.value));
        const terms = code.getTerms();

        return (terms.length === 1 && terms[0].kind === 'dice') ? code : null;
    }
}
